import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Users,
  Trash2,
  ArrowRight,
  Trophy,
  ClipboardList,
  KeyRound,
  Crown,
  UserCog,
} from "lucide-react";

export default function Dashboard() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [name, setName] = useState("");
  const [ageGroup, setAgeGroup] = useState("A-Jugend");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [joining, setJoining] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/teams");
      setTeams(data.teams);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/teams", {
        name,
        age_group: ageGroup,
        description,
      });
      toast.success("Team erstellt");
      setShowCreate(false);
      setName("");
      setDescription("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Team wirklich löschen? Alle Spieler und Ergebnisse gehen verloren."))
      return;
    try {
      await api.delete(`/teams/${id}`);
      toast.success("Team gelöscht");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const joinTeam = async (e) => {
    e.preventDefault();
    setJoining(true);
    try {
      await api.post("/teams/join", {
        invite_code: inviteCode.trim().toUpperCase(),
      });
      toast.success("Team beigetreten");
      setShowJoin(false);
      setInviteCode("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-12">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
            / TRAINER-DASHBOARD
          </div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">
            Deine Teams.
          </h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="dashboard-join-team-btn"
            onClick={() => setShowJoin(true)}
            className="btn-ghost"
          >
            <KeyRound className="w-5 h-5" />
            Team beitreten
          </button>
          <button
            data-testid="dashboard-create-team-btn"
            onClick={() => setShowCreate(true)}
            className="btn-primary"
          >
            <Plus className="w-5 h-5" />
            Neues Team
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-white/40 font-display tracking-widest">Wird geladen …</div>
      ) : teams.length === 0 ? (
        <EmptyState onCreate={() => setShowCreate(true)} />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((t) => (
            <div
              key={t.id}
              data-testid={`team-card-${t.id}`}
              className={`relative p-6 border rounded-md transition-colors group ${
                t.is_owner
                  ? "border-white/10 bg-[#111214] hover:border-[#CCFF00]/40"
                  : "border-[#00F0FF]/30 bg-[#00F0FF]/[0.03] hover:border-[#00F0FF]/60"
              }`}
            >
              {!t.is_owner && (
                <div
                  className="absolute top-0 left-0 h-full w-1 bg-[#00F0FF] rounded-l-md"
                  aria-hidden="true"
                />
              )}
              <div className="flex items-start justify-between mb-4">
                <div
                  className={`w-11 h-11 border rounded-sm flex items-center justify-center ${
                    t.is_owner
                      ? "border-[#CCFF00]/40 text-[#CCFF00]"
                      : "border-[#00F0FF]/40 text-[#00F0FF]"
                  }`}
                >
                  <Trophy className="w-5 h-5" />
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 font-display text-[10px] tracking-widest px-2 py-1 rounded-sm border ${
                      t.is_owner
                        ? "border-[#CCFF00]/40 text-[#CCFF00] bg-[#CCFF00]/5"
                        : "border-[#00F0FF]/40 text-[#00F0FF] bg-[#00F0FF]/5"
                    }`}
                    data-testid={`team-role-badge-${t.id}`}
                  >
                    {t.is_owner ? (
                      <>
                        <Crown className="w-3 h-3" />
                        OWNER
                      </>
                    ) : (
                      <>
                        <UserCog className="w-3 h-3" />
                        CO-TRAINER
                      </>
                    )}
                  </span>
                  {t.is_owner && (
                    <button
                      data-testid={`team-delete-${t.id}`}
                      onClick={() => remove(t.id)}
                      className="text-white/30 hover:text-red-400 transition-colors p-1"
                      title="Löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <h3 className="font-heading font-bold text-xl mb-1">{t.name}</h3>
              <div className="text-xs text-white/50 mb-4">
                {t.age_group} {t.description && `· ${t.description}`}
              </div>
              <div className="flex items-center gap-4 text-sm mb-5">
                <div className="flex items-center gap-1.5 text-white/60">
                  <Users className="w-4 h-4" />
                  {t.player_count} Spieler
                </div>
                <div className="flex items-center gap-1.5 text-[#CCFF00]">
                  <ClipboardList className="w-4 h-4" />
                  {t.completed_count} getestet
                </div>
                <div className="flex items-center gap-1.5 text-white/60">
                  <UserCog className="w-4 h-4" />
                  {t.member_count}/5
                </div>
              </div>
              <Link
                to={`/teams/${t.id}`}
                data-testid={`team-open-${t.id}`}
                className="inline-flex items-center gap-2 text-sm font-heading font-bold text-white hover:text-[#CCFF00] transition-colors"
              >
                Team öffnen
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
             onClick={() => setShowCreate(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={create}
            data-testid="create-team-modal"
            className="w-full max-w-md bg-[#111214] border border-white/10 rounded-md p-8"
          >
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-2">
              / NEUES TEAM
            </div>
            <h2 className="font-heading font-black text-2xl mb-6">
              Team erstellen
            </h2>
            <div className="space-y-4">
              <div>
                <label className="font-display text-xs tracking-widest text-white/60 block mb-2">
                  Name des Teams
                </label>
                <input
                  data-testid="create-team-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="z.B. U17 Herrsching"
                  className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                />
              </div>
              <div>
                <label className="font-display text-xs tracking-widest text-white/60 block mb-2">
                  Altersklasse
                </label>
                <select
                  data-testid="create-team-age-group"
                  value={ageGroup}
                  onChange={(e) => setAgeGroup(e.target.value)}
                  className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                >
                  {["C-Jugend", "B-Jugend", "A-Jugend", "Herren", "Damen", "Sonstige"].map(
                    (o) => (
                      <option key={o}>{o}</option>
                    )
                  )}
                </select>
              </div>
              <div>
                <label className="font-display text-xs tracking-widest text-white/60 block mb-2">
                  Beschreibung (optional)
                </label>
                <input
                  data-testid="create-team-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-3 text-white focus:border-[#CCFF00] outline-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="btn-ghost flex-1 justify-center"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="create-team-submit"
                disabled={creating}
                className="btn-primary flex-1 justify-center"
              >
                {creating ? "Erstellen …" : "Team erstellen"}
              </button>
            </div>
          </form>
        </div>
      )}

      {showJoin && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
             onClick={() => setShowJoin(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={joinTeam}
            data-testid="join-team-modal"
            className="w-full max-w-md bg-[#111214] border border-white/10 rounded-md p-8"
          >
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-2">
              / TEAM BEITRETEN
            </div>
            <h2 className="font-heading font-black text-2xl mb-2">
              Einladungs-Code eingeben
            </h2>
            <p className="text-sm text-white/60 mb-6">
              Frag den Team-Owner (Haupttrainer) nach dem 6-stelligen Code.
              Bis zu 5 Trainer pro Team.
            </p>
            <div>
              <label className="font-display text-xs tracking-widest text-white/60 block mb-2">
                Einladungs-Code
              </label>
              <input
                data-testid="join-team-code"
                required
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="z.B. A3F9K2"
                maxLength={12}
                className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-4 py-4 text-white text-center font-heading font-black text-2xl tracking-[0.4em] focus:border-[#CCFF00] outline-none"
              />
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowJoin(false)}
                className="btn-ghost flex-1 justify-center"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="join-team-submit"
                disabled={joining || !inviteCode.trim()}
                className="btn-primary flex-1 justify-center disabled:opacity-40"
              >
                {joining ? "Beitreten …" : "Beitreten"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="border border-white/10 border-dashed rounded-lg p-16 text-center">
      <div className="w-16 h-16 mx-auto border border-[#CCFF00]/40 rounded-sm flex items-center justify-center text-[#CCFF00] mb-6">
        <Trophy className="w-7 h-7" />
      </div>
      <h3 className="font-heading font-bold text-2xl mb-2">Noch keine Teams</h3>
      <p className="text-white/60 mb-6 max-w-md mx-auto">
        Lege dein erstes Team an, füge Spieler hinzu und starte die
        FMP-Auswertung.
      </p>
      <button
        data-testid="empty-create-team-btn"
        onClick={onCreate}
        className="btn-primary"
      >
        <Plus className="w-5 h-5" />
        Erstes Team anlegen
      </button>
    </div>
  );
}
